import {
  ISubTaskQuestionDenormalized,
  RequirementType,
  TextQuestionValidationType
} from '@dadohr/shared/domainTypes/experience';
import DateFnsUtils from '@date-io/moment';
import { IconButton, TextField } from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { validate as isValidEmail } from 'email-validator';
import { X } from 'lucide-react';
import moment from 'moment-timezone';
import { useRef } from 'react';
import { DatePickerReadonlyInput } from '../../../../../components/DatePickerReadonlyInput';
import { FormField } from '../../../../../components/Form/FormField';
import { useTranslation, useUnsafeTranslation } from '../../../../../i18next';
import { QuestionLabel } from '../Task';

/**
 * Shared component for rendering question input fields.
 * Used by both SubTaskQuestionSingle and SubTaskQuestionMulti.
 */
export const SubTaskQuestionField = ({
  s,
  qId,
  disabled,
  highlight,
  mode,
  dateFormat = 'MM/DD/YYYY'
}: {
  s: ISubTaskQuestionDenormalized;
  qId: string;
  disabled: boolean;
  highlight: boolean;
  mode: 'single' | 'multi';
  dateFormat?: string;
}) => {
  const { t } = useTranslation();
  const { t: ut } = useUnsafeTranslation();
  const { question } = s.data;

  const pickerRef = useRef<any>(null);

  const displayFormat = 'MMMM DD, YYYY';
  // Storage format - org's date format (e.g., MM/DD/YYYY)
  const storageFormat = dateFormat;

  const renderDatePicker = (
    value: string,
    onChange: (value: string) => void,
    meta: any,
    hasValue: boolean
  ) => (
    <div style={{ position: 'relative' }}>
      <DatePickerReadonlyInput
        ref={pickerRef}
        id={qId}
        fullWidth
        autoOk
        format={displayFormat}
        inputVariant="outlined"
        variant="inline"
        placeholder={displayFormat}
        error={meta.dirty && !!meta.error}
        helperText={meta.dirty && meta.error ? meta.error : ''}
        value={value ? moment(value, storageFormat) : null}
        disabled={disabled}
        onChange={(date) => {
          if (date === null) {
            onChange('');
          } else if (date && date.isValid()) {
            onChange(date.format(storageFormat));
          } else {
            onChange('');
          }
        }}
        autoFocus={highlight}
        KeyboardButtonProps={{
          'aria-label': 'change date'
        }}
      />
      {hasValue && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onChange('');
          }}
          disabled={disabled}
          aria-label="clear date"
          style={{
            position: 'absolute',
            right: 48,
            top: 4,
            padding: 4
          }}
        >
          <X size={24} />
        </IconButton>
      )}
    </div>
  );

  // TEXT question
  if (question.type === 'text') {
    const validationType =
      question.validationType || TextQuestionValidationType.NONE;

    // DATE validation - use date picker
    if (validationType === TextQuestionValidationType.DATE) {
      if (mode === 'single') {

        return (
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <QuestionLabel htmlFor={qId}>{question.label}</QuestionLabel>
            <FormField<string>
              name="answer"
              validate={(input) => {
                if (s.requirementType !== RequirementType.MANDATORY && (!input || input === '')) {
                  return undefined;
                }

                return input && input !== '' ? undefined : ut('Please select a date');
              }}
            >
              {({ input, meta }) =>
                renderDatePicker(input.value, input.onChange, meta, !!input.value)
              }
            </FormField>
          </MuiPickersUtilsProvider>
        );
      } else {
        // multi mode with date
        return (
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <QuestionLabel htmlFor={qId}>{question.label}</QuestionLabel>
            <FormField<{ value: string; type: 'text' }>
              name={s.id}
              validate={(input) => {
                if (s.requirementType !== RequirementType.MANDATORY && (!input || !input.value || input.value === '')) {
                  return undefined;
                }

                return input && input.value && input.value !== ''
                  ? undefined
                  : ut('Select a date');
              }}
            >
              {({ input, meta }) =>
                renderDatePicker(
                  input.value.value,
                  (newValue) =>
                    input.onChange({
                      type: 'text',
                      value: newValue
                    }),
                  meta,
                  !!input.value.value
                )
              }
            </FormField>
          </MuiPickersUtilsProvider>
        );
      }
    }

    // EMAIL or NONE validation - use text field
    if (mode === 'single') {
      return (
        <>
          <QuestionLabel htmlFor={qId}>{question.label}</QuestionLabel>
          <FormField<string>
            name="answer"
            validate={(input) => {
              if (s.requirementType !== RequirementType.MANDATORY && (!input || input.trim() === '')) {
                return undefined;
              }
              if (validationType === TextQuestionValidationType.EMAIL) {
                return input && isValidEmail(input)
                  ? undefined
                  : ut('Please enter an email address');
              }
              return undefined;
            }}
          >
            {({ input, meta }) => (
              <TextField
                id={qId}
                fullWidth
                variant="outlined"
                multiline
                rows={question.rows}
                maxRows={10}
                value={input.value}
                placeholder={question.placeholder}
                onChange={input.onChange}
                autoFocus={highlight}
                disabled={disabled}
                error={meta.dirty && !!meta.error}
                helperText={meta.dirty && meta.error ? meta.error : ''}
                InputProps={{
                  readOnly: disabled
                }}
                type={'text'}
              />
            )}
          </FormField>
        </>
      );
    } else {
      // multi mode EMAIL or NONE
      return (
        <>
          <QuestionLabel htmlFor={qId}>{question.label}</QuestionLabel>
          <FormField<{ value: string; type: 'text' }>
            name={s.id}
            validate={(input) => {
              if (s.requirementType !== RequirementType.MANDATORY && (!input || !input.value || input.value.trim() === '')) {
                return undefined;
              }
              if (validationType === TextQuestionValidationType.EMAIL) {
                return input && input.value && isValidEmail(input.value)
                  ? undefined
                  : ut('Please enter an email address');
              }
              return undefined;
            }}
          >
            {({ input, meta }) => (
              <TextField
                id={qId}
                fullWidth
                variant="outlined"
                multiline
                rows={question.rows}
                maxRows={10}
                value={input.value.value}
                placeholder={question.placeholder}
                onChange={(ev) =>
                  input.onChange({
                    type: 'text',
                    value: ev.target.value
                  })
                }
                error={meta.dirty && !!meta.error}
                helperText={meta.dirty && meta.error ? meta.error : ''}
                autoFocus={highlight}
                disabled={disabled}
                InputProps={{
                  readOnly: disabled
                }}
              />
            )}
          </FormField>
        </>
      );
    }
  }

  // For now, return null for other question types
  // We'll add them as needed
  return null;
};
